import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Can from '../../auth/Can';
import { listRoles, deleteRole } from '../../api/roles';
import { pushToast } from '../../components/ui/toastBus';
import RoleFormModal from './RoleFormModal';

export default function RolesList() {
  const queryClient = useQueryClient();
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: listRoles });

  const [formState, setFormState] = useState(null); // null | { roleId?: number }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      pushToast({ type: 'success', message: 'Role deleted.' });
      setDeleteTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not delete role.' });
      setDeleteTarget(null);
    },
  });

  const columns = [
    {
      key: 'name',
      label: 'Role',
      render: (row) => (
        <span className="flex items-center gap-2 font-medium text-gray-900">
          {row.name}
          {row.is_system && <Badge tone="amber">system</Badge>}
        </span>
      ),
    },
    { key: 'permissions_count', label: 'Permissions' },
    { key: 'users_count', label: 'Users' },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-3 text-sm">
          <Can perm="roles.update">
            <button onClick={() => setFormState({ roleId: row.id })} className="text-primary hover:underline">
              Edit
            </button>
          </Can>
          <Can perm="roles.delete">
            <button
              onClick={() => setDeleteTarget(row)}
              disabled={row.is_system || row.users_count > 0}
              className="text-red-600 hover:underline disabled:cursor-not-allowed disabled:text-gray-300"
              title={
                row.is_system
                  ? 'System role cannot be deleted'
                  : row.users_count > 0
                    ? 'Role has users attached'
                    : undefined
              }
            >
              Delete
            </button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Roles & Permissions"
        actions={
          <Can perm="roles.create">
            <Button onClick={() => setFormState({})}>Create role</Button>
          </Can>
        }
      />

      <Table columns={columns} data={rolesQuery.data || []} isLoading={rolesQuery.isLoading} emptyMessage="No roles yet." />

      {formState && (
        <RoleFormModal open={!!formState} roleId={formState.roleId} onClose={() => setFormState(null)} />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete role"
        message={`Delete the "${deleteTarget?.name}" role? This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
